import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Percent, TrendingUp, Megaphone, Handshake, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import MultiStepForm from "@/components/forms/MultiStepForm";
import { stagger, fadeUp } from "@/lib/motion";

const BENEFITS = [
  { icon: Percent, title: "Up to 50% Commission", text: "Industry-leading margins for serious partners." },
  { icon: TrendingUp, title: "High Demand Products", text: "Fast-moving car care range with repeat sales." },
  { icon: Megaphone, title: "Marketing Support", text: "Creatives, training and campaign assistance." },
  { icon: Handshake, title: "Long-Term Partnership", text: "Grow together with dedicated account support." },
];

const STEPS = [
  {
    title: "Basic Information",
    subtitle: "Tell us who you are.",
    fields: [
      { name: "fullName", label: "Full Name", type: "text", required: true, placeholder: "Your full name" },
      { name: "companyName", label: "Company / Business Name", type: "text", placeholder: "Business name" },
      { name: "mobile", label: "Mobile Number", type: "tel", required: true, placeholder: "+91" },
      { name: "email", label: "Email Address", type: "email", required: true, placeholder: "you@email.com" },
      { name: "cityState", label: "City & State", type: "text", placeholder: "City, State" },
      { name: "website", label: "Website / Social Media", type: "text", placeholder: "https://" },
    ],
  },
  {
    title: "Business Profile",
    subtitle: "Help us understand your business.",
    fields: [
      { name: "businessType", label: "What best describes your business?", type: "radio", full: true, options: ["Distributor", "Retailer", "Workshop / Service Center", "E-commerce Seller", "Car Detailing Studio", "Other"] },
      { name: "yearsInIndustry", label: "Years in the automotive industry", type: "select", options: ["Less than 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"] },
      { name: "categoriesDealt", label: "Categories you currently deal in", type: "checkbox", full: true, options: ["Car care products", "Polishes & compounds", "Cleaning chemicals", "Accessories"] },
      { name: "similarBrands", label: "Do you currently work with similar brands?", type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Market Reach & Capability",
    subtitle: "Your scale and coverage.",
    fields: [
      { name: "regions", label: "Which regions do you serve?", type: "select", options: ["City-level", "State-level", "Multi-state", "Pan-India"] },
      { name: "customerBase", label: "Type of customer base", type: "radio", options: ["B2B", "B2C", "Both"] },
      { name: "monthlySales", label: "Approximate monthly sales volume", type: "radio", full: true, options: ["< ₹2.5L", "₹2.5L - ₹10L", "₹10L - ₹25L", "₹25L+"] },
      { name: "distributionNetwork", label: "Existing distribution network?", type: "radio", options: ["Yes", "No"] },
      { name: "warehouse", label: "Warehouse / inventory capability?", type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Sales Intent & Alignment",
    subtitle: "Why AutoCareX, and how you'll sell.",
    fields: [
      { name: "whyPartner", label: "Why are you interested in partnering with AutoCareX?", type: "textarea", full: true, placeholder: "Share your motivation..." },
      { name: "productInterest", label: "Product categories you're most interested in", type: "checkbox", full: true, options: ["Polishes", "Aerosols", "Cleaning & detailing products"] },
      { name: "howSell", label: "How do you plan to sell our products?", type: "checkbox", full: true, options: ["Offline distribution", "Workshop tie-ups", "Online marketplaces", "Direct retail"] },
      { name: "expectedOrderQty", label: "Expected monthly order quantity", type: "select", options: ["< 100 units", "100-500 units", "500-2000 units", "2000+ units"] },
    ],
  },
  {
    title: "Operational Readiness",
    subtitle: "How ready you are to execute.",
    fields: [
      { name: "salesTeam", label: "Do you have a dedicated sales team?", type: "radio", options: ["Yes", "No"] },
      { name: "moqOpen", label: "Open to meeting minimum order quantities (MOQ)?", type: "radio", options: ["Yes", "No"] },
      { name: "logistics", label: "Logistics capability", type: "radio", options: ["Self-managed", "Need support"] },
    ],
  },
  {
    title: "Commercial Understanding",
    subtitle: "Final alignment.",
    fields: [
      { name: "commissionComfort", label: "Comfortable with a commission-based model (up to 50%)?", type: "radio", options: ["Yes", "No"] },
      { name: "partnershipType", label: "Preferred partnership type", type: "radio", full: true, options: ["Distributor", "Reseller", "Channel Partner", "Affiliate"] },
    ],
  },
];

export default function Partner() {
  const handleSubmit = async (values) => {
    const res = await api.createPartnerLead(values);
    toast.success(res.message || "Application submitted");
  };

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="Partner With Us" description="Become an AutoCareX partner — up to 50% commission, high-demand products and marketing support." />

      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_20%_0%,rgba(225,27,34,0.12),transparent_55%)]" />
        <div className="acx-container relative px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">Business Partnership</span>
            <h1 className="mt-3 max-w-3xl font-display text-[44px] font-extrabold leading-[1.02] tracking-[-0.03em] sm:text-6xl">Partner with us.</h1>
            <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-white/65">
              Join a fast-growing automotive care brand. Enjoy strong margins, high-demand products and
              dedicated support to scale your business with AutoCareX.
            </p>
          </Reveal>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <motion.div key={b.title} variants={fadeUp} className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E11B22]/15 text-[#E11B22]"><b.icon size={20} /></span>
                <p className="mt-4 font-display text-[16px] font-semibold text-white">{b.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">{b.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
          <div className="mx-auto max-w-3xl">
            <Reveal className="mb-8 text-center">
              <h2 className="font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">Partner Application</h2>
              <p className="mt-3 text-[15px] text-white/55">Complete the form below and our team will get in touch.</p>
            </Reveal>
            <MultiStepForm steps={STEPS} onSubmit={handleSubmit} submitLabel="Submit Partner Application" testId="partner-multistep-form" />
          </div>
        </div>
      </section>
    </div>
  );
}
